import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdAttachFile, MdChatBubble, MdDownload, MdGroups, MdSchedule, MdSearch, MdSend } from 'react-icons/md';
import { getData, postData, postFileUpload } from '../../../Webservice/Webservice';
import { API_URL } from '../../../Utils/url';
import { DB_FIELDS, FONT, VariableEditor, WA_GREEN, WaBubble, authToken, fillWa, isDynamic } from './waShared';

const EMPTY_FILTERS = { promotionType: 'whatsapp', city: [], gender: [], status: [], customerType: [] };

const Section = ({ n, title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{n}. {title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const MultiSelect = ({ label, value, onChange, options, render }) => (
  <TextField select fullWidth size="small" label={label} value={value} SelectProps={{ multiple: true, renderValue: render || ((sel) => sel.join(', ')) }} onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        <Checkbox checked={value.includes(o.value)} size="small" />
        <ListItemText primary={o.label} />
      </MenuItem>
    ))}
  </TextField>
);

function WhatsappForm({ templates, onBack, onCreated }) {
  const [name, setName] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [values, setValues] = useState({});
  const [headerFile, setHeaderFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState('');
  const [mode, setMode] = useState('now'); // now | schedule | draft
  const [scheduledAt, setScheduledAt] = useState('');

  const [source, setSource] = useState('database'); // database | excel
  const [excelFile, setExcelFile] = useState(null);
  const [excelFields, setExcelFields] = useState([]);
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const approved = templates.filter((t) => t.status === 'APPROVED');
  const template = templates.find((t) => t._id === templateName);
  const needsMedia = template && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.headerType);
  const fields = source === 'excel' ? excelFields : DB_FIELDS;

  const fetchCustomers = async (f) => {
    setLoadingCustomers(true);
    try {
      const res = await postData('campaign/customer/list', f, authToken(), false);
      const unique = new Map();
      (res?.customers || []).forEach((c) => {
        const key = (c.mobile || '').trim();
        if (key && !unique.has(key)) unique.set(key, c);
      });
      const list = Array.from(unique.values());
      setCustomers(list);
      setSelected(list.map((c) => c._id));
    } catch (e) {
      setCustomers([]);
      setSelected([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    getData('campaign/city/list', null, authToken(), false).then((r) => setCities(r?.cities || [])).catch(() => setCities([]));
    fetchCustomers(EMPTY_FILTERS);
  }, []);

  const pickTemplate = (id) => {
    const t = templates.find((x) => x._id === id);
    setTemplateName(id);
    setValues((t?.variables || []).reduce((acc, v) => ({ ...acc, [v]: '' }), {}));
    setHeaderFile(null);
    setHeaderPreview('');
    setErrors((p) => ({ ...p, template: undefined }));
  };

  const onExcel = (file) => {
    setExcelFile(file);
    setErrors((p) => ({ ...p, audience: undefined }));
    if (!file) return setExcelFields([]);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const range = XLSX.utils.decode_range(ws['!ref']);
        const headers = [];
        for (let c = range.s.c; c <= range.e.c; c += 1) {
          const cell = ws[XLSX.utils.encode_cell({ c, r: range.s.r })];
          if (cell && cell.v) headers.push(cell.v.toString().trim());
        }
        setExcelFields(headers);
      } catch (e) {
        setExcelFields([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  const onHeaderFile = (file) => {
    setHeaderFile(file);
    setHeaderPreview(file && file.type.startsWith('image') ? URL.createObjectURL(file) : '');
    setErrors((p) => ({ ...p, header: undefined }));
  };

  const filteredPicker = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return q ? customers.filter((c) => `${c.name || ''} ${c.mobile || ''}`.toLowerCase().includes(q)) : customers;
  }, [customers, pickerSearch]);

  const bodyPreview = template ? fillWa(template.content || '', values) : '';

  const submit = async () => {
    const next = {};
    if (!name.trim()) next.name = 'Give the campaign a name';
    if (!template) next.template = 'Choose an approved template';
    const missing = (template?.variables || []).filter((v) => !String(values[v] || '').trim());
    if (missing.length) next.values = `Fill in: ${missing.map((m) => `{{${m}}}`).join(', ')}`;
    if (needsMedia && !headerFile) next.header = `This template needs a ${template.headerType.toLowerCase()} for its header`;
    if (source === 'database' && !selected.length) next.audience = 'Select at least one customer';
    if (source === 'excel' && !excelFile) next.audience = 'Upload an Excel file with the contacts';
    if (source === 'excel' && excelFile && (template?.variables || []).some((v) => isDynamic(values[v]) && !excelFields.includes(values[v].replace(/^\{\{dynamic:|\}\}$/g, '')))) next.values = 'A "from customer" value uses a column that is not in your Excel file';
    if (mode === 'schedule') {
      if (!scheduledAt) next.scheduledAt = 'Choose when to send';
      else if (new Date(scheduledAt).getTime() < Date.now()) next.scheduledAt = 'That time has already passed';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('templateName', templateName);
      fd.append('templateLanguage', template?.language || 'en_US');
      fd.append('variableValues', JSON.stringify(values));
      fd.append('mode', mode);
      if (mode === 'schedule') fd.append('scheduledAt', new Date(scheduledAt).toISOString());
      if (needsMedia) {
        fd.append('headerMediaType', template.headerType);
        if (headerFile) fd.append('headerMedia', headerFile);
      }
      if (source === 'excel') fd.append('file', excelFile);
      else fd.append('customerIds', JSON.stringify(selected));
      await postFileUpload('whatsapp-campaign/create', fd, authToken());
      onCreated();
    } catch (e) {
      /* the shared service already showed the error */
    } finally {
      setSaving(false);
    }
  };

  const count = source === 'excel' ? (excelFile ? 'from your file' : '-') : selected.length.toLocaleString('en-IN');

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={onBack} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>New WhatsApp Campaign</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Pick an approved template, choose who gets it, fill in its values, then send, schedule or save a draft.</Typography>
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Section n={1} title="Campaign & template" hint="Only templates approved by Meta can be sent" accent="#6366f1" icon={MdChatBubble}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Campaign name" required value={name} onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }} error={!!errors.name} helperText={errors.name || 'Only you see this'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select fullWidth size="small" label="WhatsApp template" required value={templateName} onChange={(e) => pickTemplate(e.target.value)} error={!!errors.template} helperText={errors.template || (approved.length ? `${approved.length} approved template(s)` : 'No approved templates yet. Add one on the Templates tab.')}>
                      {approved.map((t) => <MenuItem key={t._id} value={t._id}>{t.name} · {t.language}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
              </Section>

              <Section n={2} title="Audience" hint="Customers from your database, or contacts from an Excel sheet" accent="#0ea5e9" icon={MdGroups}>
                <RadioGroup row value={source} onChange={(e) => { setSource(e.target.value); setErrors((p) => ({ ...p, audience: undefined })); }} sx={{ gap: 1, mb: 2 }}>
                  {[['database', 'Database customers', 'Filter by city, gender and type'], ['excel', 'Excel upload (.xlsx)', 'Any list with Name and Mobile columns']].map(([v, label, sub]) => (
                    <Box key={v} onClick={() => setSource(v)} sx={{ flex: '1 1 240px', cursor: 'pointer', p: 1.25, borderRadius: '12px', border: `1.5px solid ${source === v ? WA_GREEN : '#e2e8f0'}`, background: source === v ? 'rgba(37,211,102,.08)' : '#fff' }}>
                      <FormControlLabel value={v} control={<Radio size="small" sx={{ color: WA_GREEN, '&.Mui-checked': { color: WA_GREEN } }} />} label={<Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{label}</Typography>} sx={{ m: 0 }} />
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b', ml: 3.5 }}>{sub}</Typography>
                    </Box>
                  ))}
                </RadioGroup>

                {source === 'database' ? (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}><MultiSelect label="City" value={filters.city} onChange={(v) => setFilters((p) => ({ ...p, city: v }))} options={cities.map((c) => ({ value: c, label: c }))} render={(sel) => (sel.length === cities.length && cities.length ? 'All cities' : sel.join(', '))} /></Grid>
                      <Grid item xs={12} md={6}><MultiSelect label="Gender" value={filters.gender} onChange={(v) => setFilters((p) => ({ ...p, gender: v }))} options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} /></Grid>
                      <Grid item xs={12} md={6}><MultiSelect label="Customer type" value={filters.customerType} onChange={(v) => setFilters((p) => ({ ...p, customerType: v }))} options={[{ value: 'customer', label: 'Customer (B2C)' }, { value: 'business', label: 'Business (B2B)' }]} /></Grid>
                      <Grid item xs={12} md={6}><MultiSelect label="Account status" value={filters.status} onChange={(v) => setFilters((p) => ({ ...p, status: v }))} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} /></Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                      <Button onClick={() => fetchCustomers(filters)} disabled={loadingCustomers} variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#0f172a', borderColor: '#cbd5e1' }}>{loadingCustomers ? 'Loading…' : 'Apply filters'}</Button>
                      <Button onClick={() => setPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0f2fe', color: '#075985', px: 2 }}>{selected.length} of {customers.length} customers selected · review</Button>
                    </Box>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 1 }}>Only customers who accepted WhatsApp promotions are listed.</Typography>
                  </>
                ) : (
                  <Box sx={{ p: 3, border: '2px dashed #cbd5e1', borderRadius: '14px', background: '#f8fafc', textAlign: 'center' }}>
                    <Button component="label" startIcon={<MdAttachFile />} variant="outlined" sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', borderColor: WA_GREEN, color: '#128c7e' }}>
                      {excelFile ? excelFile.name : 'Choose Excel file'}
                      <input type="file" hidden accept=".xlsx" onChange={(e) => onExcel(e.target.files?.[0] || null)} />
                    </Button>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 1.5 }}>The first row must have the headers <b>Name</b> and <b>Mobile</b> (with country code, e.g. 919876543210). Extra columns can be used as variables.</Typography>
                    <Button startIcon={<MdDownload />} href={`${API_URL}whatsapp-campaign/download-template`} target="_blank" sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}>Download sample file</Button>
                    {excelFields.length > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#059669', mt: 1 }}>Columns found: {excelFields.join(', ')}</Typography>}
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#b45309', mt: 1 }}>People in an uploaded list are not checked against WhatsApp opt-in. Make sure they agreed to hear from you.</Typography>
                  </Box>
                )}
                {errors.audience && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 1 }}>{errors.audience}</Typography>}
              </Section>

              {template && (
                <Section n={3} title="Message values" hint="Fill in what each placeholder in the template should say" accent={WA_GREEN} icon={MdChatBubble}>
                  {(template.variables || []).length === 0 ? (
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>This template has no variables to fill in.</Typography>
                  ) : (
                    <VariableEditor variables={template.variables} values={values} onChange={(v) => { setValues(v); setErrors((p) => ({ ...p, values: undefined })); }} fields={fields.length ? fields : DB_FIELDS} />
                  )}
                  {errors.values && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 1 }}>{errors.values}</Typography>}
                  {needsMedia && (
                    <Box sx={{ mt: 2.5, p: 2, border: `1px dashed ${errors.header ? '#ef4444' : '#cbd5e1'}`, borderRadius: '12px' }}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5, mb: 0.5 }}>Header {template.headerType.toLowerCase()}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mb: 1 }}>This template shows a {template.headerType.toLowerCase()} above the text.</Typography>
                      <Button component="label" startIcon={<MdAttachFile />} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', background: '#f1f5f9', color: '#0f172a', px: 2 }}>
                        {headerFile ? headerFile.name : `Upload ${template.headerType.toLowerCase()}`}
                        <input type="file" hidden onChange={(e) => onHeaderFile(e.target.files?.[0] || null)} />
                      </Button>
                      {errors.header && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.75 }}>{errors.header}</Typography>}
                    </Box>
                  )}
                </Section>
              )}

              <Section n={template ? 4 : 3} title="When to send" hint="Send right away, schedule it, or save a draft to review first" accent="#10b981" icon={MdSchedule}>
                <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)} sx={{ gap: 1 }}>
                  {[['now', 'Send now', 'Starts within a minute'], ['schedule', 'Schedule', 'Pick a date and time'], ['draft', 'Save as draft', 'Review and start later']].map(([v, label, sub]) => (
                    <Box key={v} onClick={() => setMode(v)} sx={{ flex: '1 1 180px', cursor: 'pointer', p: 1.25, borderRadius: '12px', border: `1.5px solid ${mode === v ? 'rgb(255 190 0)' : '#e2e8f0'}`, background: mode === v ? 'rgb(255 209 2 / 12%)' : '#fff' }}>
                      <FormControlLabel value={v} control={<Radio size="small" sx={{ color: 'rgb(255 190 0)', '&.Mui-checked': { color: 'rgb(255 190 0)' } }} />} label={<Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{label}</Typography>} sx={{ m: 0 }} />
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b', ml: 3.5 }}>{sub}</Typography>
                    </Box>
                  ))}
                </RadioGroup>
                {mode === 'schedule' && (
                  <TextField fullWidth size="small" type="datetime-local" label="Send at" InputLabelProps={{ shrink: true }} inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) }} value={scheduledAt} onChange={(e) => { setScheduledAt(e.target.value); setErrors((p) => ({ ...p, scheduledAt: undefined })); }} error={!!errors.scheduledAt} helperText={errors.scheduledAt} sx={{ mt: 2 }} />
                )}
              </Section>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: 'sticky' }, top: 16, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1.5 }}>Message preview</Typography>
                <WaBubble headerType={template?.headerType} headerImage={headerPreview} body={bodyPreview} empty={template ? 'This template has no body text.' : 'Choose a template to see the message'} />
                {bodyPreview.includes('{{') && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#b45309', mt: 1 }}>Some values are still empty (shown as {'{{1}}'}). A value taken from the customer shows as [field].</Typography>}
              </Paper>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Summary</Typography>
                {[['Recipients', count], ['Delivery', mode === 'now' ? 'Starts within a minute' : mode === 'draft' ? 'Saved as a draft' : scheduledAt ? new Date(scheduledAt).toLocaleString('en-IN') : 'Pick a time']].map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{k}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700 }}>{v}</Typography>
                  </Box>
                ))}
                <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8', mt: 1 }}>Marketing messages are charged by Meta per conversation. Messages can't be recalled once sent.</Typography>
              </Paper>
              <Button onClick={submit} disabled={saving} variant="contained" startIcon={saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : mode === 'schedule' ? <MdSchedule /> : <MdSend />} sx={{ fontFamily: FONT, textTransform: 'none', background: WA_GREEN, color: '#fff', py: 1.4, borderRadius: '12px', fontWeight: 800, fontSize: 13.5, boxShadow: '0 4px 14px rgba(37,211,102,.39)', '&:hover': { background: '#1ebe57' } }}>
                {saving ? 'Working…' : mode === 'now' ? 'Create & send now' : mode === 'schedule' ? 'Schedule campaign' : 'Save draft'}
              </Button>
              <Button onClick={onBack} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Recipients ({selected.length} of {customers.length})</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" placeholder="Search name or mobile" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} InputProps={{ startAdornment: <MdSearch style={{ marginRight: 8, color: '#94a3b8' }} /> }} sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => setSelected(customers.map((c) => c._id))} sx={{ textTransform: 'none', fontWeight: 700 }}>Select all</Button>
            <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>
          </Box>
          <Box sx={{ maxHeight: 380, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
            {filteredPicker.length === 0 ? <Typography sx={{ p: 2, fontFamily: FONT, color: '#64748b' }}>No customers match.</Typography> : filteredPicker.map((c) => (
              <Box key={c._id} onClick={() => setSelected((p) => (p.includes(c._id) ? p.filter((x) => x !== c._id) : [...p, c._id]))} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', '&:hover': { background: '#f8fafc' } }}>
                <Checkbox checked={selected.includes(c._id)} size="small" disableRipple />
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 12.5 }}>{c.name || 'Customer'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{c.mobile}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPickerOpen(false)} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', '&:hover': { background: 'rgb(255 190 0)' } }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WhatsappForm;
